import { createSearchParams } from "@/utils";
import { tripApi } from "../tripApi";
import {
  transformFixedPricingStates,
  transformDynamicPricingStates,
  transformPricingProfiles,
  transformNationwideFixedPricing,
  transformFixedPricingLocations,
  transformPricingProfile,
} from "./helpers";
import {
  GetDynamicPricingStatesResponse,
  UpdateStateBasedDynamicPricingParams,
  UpdateStateBasedDynamicPricingResponse,
  DeleteStateBasedDynamicPricingParams,
  GetTripCostEstimateParams,
  GetTripCostEstimateResponse,
  GetPricingProfilesResponse,
  AddPricingProfileResponse,
  AddPricingProfileParams,
  EditPricingProfileResponse,
  EditPricingProfileParams,
  DeletePricingProfileParams,
  GetNationwideFixedPricingResponse,
  GetNationwideFixedPricingParams,
  UpdateNationwideFixedPricingResponse,
  UpdateNationwideFixedPricingParams,
  DeleteNationwideFixedPricingParams,
  GetFixedPricingStatesResponse,
  GetFixedPricingStatesParams,
  UpdateStateBasedFixedPricingResponse,
  UpdateStateBasedFixedPricingParams,
  DeleteStateBasedFixedPricingParams,
  GetFixedPricingLocationsResponse,
  GetFixedPricingLocationsParams,
  AddFixedPricingLocationParams,
  UpdateFixedPricingLocationResponse,
  UpdateFixedPricingLocationParams,
  UpdateLocationBasedFixedPricingResponse,
  UpdateLocationBasedFixedPricingParams,
  DeleteLocationBasedFixedPricingParams,
  GetPricingProfilesParams,
  GetPricingProfileResponse,
  AddFixedPricingLocationRawResponse,
} from "./types";

/**
 * Zone and state rate collections are paginated by the API (it defaults to 12 per page).
 * Building the path through `createSearchParams` keeps `page_size` attached — destructuring
 * only `id` used to drop it silently, capping both lists at 12 with no way to reach the rest.
 */
export const fixedPricingLocationsPath = ({ id, ...params }: GetFixedPricingLocationsParams) =>
  `/pricing/profile/fixed/${id}/location?${createSearchParams(params).toString()}`;

export const fixedPricingStatesPath = ({ id, ...params }: GetFixedPricingStatesParams) =>
  `/pricing/profile/fixed/${id}/state?${createSearchParams(params).toString()}`;

export const tripCostApi = tripApi
  .enhanceEndpoints({
    addTagTypes: [
      "DynamicPricingState",
      "PricingProfiles",
      "PricingProfile",
      "NationwideFixedPricing",
      "FixedPricingState",
      "FixedPricingLocation",
    ],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getDynamicPricingStates: builder.query<GetDynamicPricingStatesResponse, void>({
        query: () => "/pricing/standard?page_size=100",
        transformResponse: transformDynamicPricingStates,
        providesTags: ["DynamicPricingState"],
      }),
      updateStateBasedDynamicPricing: builder.mutation<UpdateStateBasedDynamicPricingResponse, UpdateStateBasedDynamicPricingParams>({
        query: ({ state, ...body }) => ({
          url: `/pricing/standard/${state}`,
          method: "PUT",
          body,
        }),
        invalidatesTags: ["DynamicPricingState"],
      }),
      deleteStateBasedDynamicPricing: builder.mutation<void, DeleteStateBasedDynamicPricingParams>({
        query: ({ state }) => ({
          url: `/pricing/standard/${state}`,
          method: "DELETE",
        }),
        invalidatesTags: ["DynamicPricingState"],
      }),
      getTripCostEstimate: builder.query<GetTripCostEstimateResponse, GetTripCostEstimateParams>({
        query: (params) => ({
          url: "/config/pricing/trip-cost/estimate",
          params,
        }),
      }),
      getPricingProfiles: builder.query<GetPricingProfilesResponse, GetPricingProfilesParams>({
        query: (params) => `/pricing/profile/fixed?${createSearchParams(params).toString()}`,
        transformResponse: transformPricingProfiles,
        providesTags: ["PricingProfiles"],
      }),
      getPricingProfile: builder.query<GetPricingProfileResponse, string>({
        query: (id) => `/pricing/profile/fixed/${id}`,
        transformResponse: transformPricingProfile,
        providesTags: ["PricingProfile"],
      }),
      addPricingProfile: builder.mutation<AddPricingProfileResponse, AddPricingProfileParams>({
        query: (body) => ({
          url: "/pricing/profile/fixed",
          method: "POST",
          body,
        }),
        invalidatesTags: ["PricingProfiles"],
      }),
      editPricingProfile: builder.mutation<EditPricingProfileResponse, EditPricingProfileParams>({
        query: ({ id, ...body }) => ({
          url: `/pricing/profile/fixed/${id}`,
          method: "PUT",
          body,
        }),
        invalidatesTags: ["PricingProfiles", "PricingProfile"],
      }),
      deletePricingProfile: builder.mutation<void, DeletePricingProfileParams>({
        query: ({ id }) => ({
          url: `/pricing/profile/fixed/${id}`,
          method: "DELETE",
        }),
        invalidatesTags: ["PricingProfiles"],
      }),
      getNationwideFixedPricing: builder.query<GetNationwideFixedPricingResponse, GetNationwideFixedPricingParams>({
        query: ({ id }) => `/pricing/profile/fixed/${id}/nationwide`,
        transformResponse: transformNationwideFixedPricing,
        providesTags: ["NationwideFixedPricing"],
      }),
      updateNationwideFixedPricing: builder.mutation<UpdateNationwideFixedPricingResponse, UpdateNationwideFixedPricingParams>({
        query: ({ id, ...body }) => ({
          url: `/pricing/profile/fixed/${id}/nationwide`,
          method: "PUT",
          body,
        }),
        invalidatesTags: ["PricingProfiles"],
      }),
      deleteNationwideFixedPricing: builder.mutation<void, DeleteNationwideFixedPricingParams>({
        query: ({ id }) => ({
          url: `/pricing/profile/fixed/${id}/nationwide`,
          method: "DELETE",
        }),
        invalidatesTags: ["PricingProfiles"],
      }),
      getFixedPricingStates: builder.query<GetFixedPricingStatesResponse, GetFixedPricingStatesParams>({
        query: fixedPricingStatesPath,
        transformResponse: transformFixedPricingStates,
        providesTags: ["FixedPricingState"],
      }),
      updateStateBasedFixedPricing: builder.mutation<UpdateStateBasedFixedPricingResponse, UpdateStateBasedFixedPricingParams>({
        query: ({ id, state, ...body }) => ({
          url: `/pricing/profile/fixed/${id}/state/${state}`,
          method: "PUT",
          body,
        }),
        invalidatesTags: ["FixedPricingState", "PricingProfile", "PricingProfiles"],
      }),
      deleteStateBasedFixedPricing: builder.mutation<void, DeleteStateBasedFixedPricingParams>({
        query: ({ id, state }) => ({
          url: `/pricing/profile/fixed/${id}/state/${state}`,
          method: "DELETE",
        }),
        invalidatesTags: ["FixedPricingState", "PricingProfile", "PricingProfiles"],
      }),
      getFixedPricingLocations: builder.query<GetFixedPricingLocationsResponse, GetFixedPricingLocationsParams>({
        query: fixedPricingLocationsPath,
        transformResponse: transformFixedPricingLocations,
        providesTags: ["FixedPricingLocation"],
      }),
      addFixedPricingLocation: builder.mutation<AddFixedPricingLocationRawResponse, AddFixedPricingLocationParams>({
        query: ({ id, ...body }) => ({
          url: `/pricing/profile/fixed/${id}/location`,
          method: "POST",
          body,
        }),
      }),
      updateFixedPricingLocation: builder.mutation<UpdateFixedPricingLocationResponse, UpdateFixedPricingLocationParams>({
        query: ({ profileId, locationId, ...body }) => ({
          url: `/pricing/profile/fixed/${profileId}/location/${locationId}`,
          method: "POST",
          body,
        }),
      }),
      updateLocationBasedFixedPricing: builder.mutation<UpdateLocationBasedFixedPricingResponse, UpdateLocationBasedFixedPricingParams>({
        query: ({ profileId, locationId, ...body }) => ({
          url: `/pricing/profile/fixed/${profileId}/location/${locationId}`,
          method: "PUT",
          body,
        }),
        invalidatesTags: ["FixedPricingLocation", "PricingProfile", "PricingProfiles"],
      }),
      deleteLocationBasedFixedPricing: builder.mutation<void, DeleteLocationBasedFixedPricingParams>({
        query: ({ profileId, locationId }) => ({
          url: `/pricing/profile/fixed/${profileId}/location/${locationId}`,
          method: "DELETE",
        }),
        invalidatesTags: ["FixedPricingLocation", "PricingProfile", "PricingProfiles"],
      }),
    }),
  });

export const {
  useGetDynamicPricingStatesQuery,
  useUpdateStateBasedDynamicPricingMutation,
  useDeleteStateBasedDynamicPricingMutation,
  useLazyGetTripCostEstimateQuery,
  useGetPricingProfilesQuery,
  useGetPricingProfileQuery,
  useAddPricingProfileMutation,
  useEditPricingProfileMutation,
  useDeletePricingProfileMutation,
  useLazyGetNationwideFixedPricingQuery,
  useUpdateNationwideFixedPricingMutation,
  useDeleteNationwideFixedPricingMutation,
  useLazyGetFixedPricingStatesQuery,
  useUpdateStateBasedFixedPricingMutation,
  useDeleteStateBasedFixedPricingMutation,
  useLazyGetFixedPricingLocationsQuery,
  useAddFixedPricingLocationMutation,
  useUpdateFixedPricingLocationMutation,
  useUpdateLocationBasedFixedPricingMutation,
  useDeleteLocationBasedFixedPricingMutation,
} = tripCostApi;
